// Copyright (c) 2026 Endel Dreyer.
//
// This software is released under the MIT License.
// https://opensource.org/license/MIT
//
// colyseus.js@0.17.22
import { CloseCode, Protocol } from '@colyseus/shared-types';
import { Decoder, encode, decode } from '@colyseus/schema';
import { Packr, unpack } from '@colyseus/msgpackr';
import { Connection } from './Connection.mjs';
import { getSerializer } from './serializer/Serializer.mjs';
import { createNanoEvents } from './core/nanoevents.mjs';
import { createSignal } from './core/signal.mjs';
import { SchemaSerializer } from './serializer/SchemaSerializer.mjs';
import { now } from './core/utils.mjs';

class Room {
    roomId;
    sessionId;
    reconnectionToken;
    name;
    connection;
    // Public signals
    onStateChange = createSignal();
    onError = createSignal();
    onLeave = createSignal();
    onReconnect = createSignal();
    onDrop = createSignal();
    onJoin = createSignal();
    serializerId;
    serializer;
    // reconnection logic
    reconnection = {
        retryCount: 0,
        maxRetries: 15,
        delay: 100,
        minDelay: 100,
        maxDelay: 5000,
        minUptime: 5000,
        backoff: exponentialBackoff,
        maxEnqueuedMessages: 10,
        enqueuedMessages: [],
        isReconnecting: false,
    };
    joinedAtTime = 0;
    onMessageHandlers = createNanoEvents();
    packr;
    #lastPingTime = 0;
    #pingCallback = undefined;
    constructor(name, rootSchema) {
        this.name = name;
        this.packr = new Packr();
        // msgpackr workaround: force buffer to be created.
        this.packr.encode(undefined);
        if (rootSchema) {
            const serializer = new (getSerializer("schema"));
            this.serializer = serializer;
            const state = new rootSchema();
            serializer.state = state;
            serializer.decoder = new Decoder(state);
        }
        this.onLeave(() => this.removeAllListeners());
    }
    connect(endpoint, options, headers) {
        this.connection = new Connection(options.protocol);
        this.connection.events.onmessage = this.onMessageCallback.bind(this);
        this.connection.events.onclose = (e) => {
            if (this.joinedAtTime === 0) {
                console.warn?.(`Room connection was closed unexpectedly (${e.code}): ${e.reason}`);
                this.onError.invoke(e.code, e.reason);
                return;
            }
            if (e.code === CloseCode.NO_STATUS_RECEIVED ||
                e.code === CloseCode.ABNORMAL_CLOSURE ||
                e.code === CloseCode.GOING_AWAY ||
                e.code === CloseCode.MAY_TRY_RECONNECT) {
                this.onDrop.invoke(e.code, e.reason);
                this.handleReconnection();
            }
            else {
                this.onLeave.invoke(e.code, e.reason);
                this.destroy();
            }
        };
        this.connection.events.onerror = (e) => {
            this.onError.invoke(e.code, e.reason);
        };
        /**
         * if local serializer has state, it means we don't need to receive the
         * handshake from the server
         */
        const skipHandshake = (this.serializer?.getState() !== undefined);
        if (options.protocol === "h3") {
            // FIXME: refactor this.
            const url = new URL(endpoint);
            this.connection.connect(url.origin, { ...options, skipHandshake });
        }
        else {
            this.connection.connect(`${endpoint}${skipHandshake ? "?skipHandshake=1" : ""}`, headers);
        }
    }
    leave(consented = true) {
        return new Promise((resolve) => {
            this.onLeave((code) => resolve(code));
            if (this.connection) {
                if (consented) {
                    this.packr.buffer[0] = Protocol.LEAVE_ROOM;
                    this.connection.send(this.packr.buffer.subarray(0, 1));
                }
                else {
                    this.connection.close();
                }
            }
            else {
                this.onLeave.invoke(CloseCode.CONSENTED);
            }
        });
    }
    onMessage(type, callback) {
        return this.onMessageHandlers.on(this.getMessageHandlerKey(type), callback);
    }
    ping(callback) {
        // skip if connection is not open
        if (!this.connection?.isOpen) {
            return;
        }
        this.#lastPingTime = now();
        this.#pingCallback = callback;
        this.packr.buffer[0] = Protocol.PING;
        this.connection.send(this.packr.buffer.subarray(0, 1));
    }
    send(messageType, payload) {
        const it = { offset: 1 };
        this.packr.buffer[0] = Protocol.ROOM_DATA;
        if (typeof (messageType) === "string") {
            encode.string(this.packr.buffer, messageType, it);
        }
        else {
            encode.number(this.packr.buffer, messageType, it);
        }
        // force packr to use beginning of the buffer
        this.packr.position = 0;
        const data = (payload !== undefined)
            ? this.packr.pack(payload, 2048 + it.offset) // 2048 = RESERVE_START_SPACE
            : this.packr.buffer.subarray(0, it.offset);
        // If connection is not open, buffer the message
        if (!this.connection.isOpen) {
            enqueueMessage(this, new Uint8Array(data));
        }
        else {
            this.connection.send(data);
        }
    }
    sendUnreliable(type, message) {
        // If connection is not open, skip
        if (!this.connection.isOpen) {
            return;
        }
        const it = { offset: 1 };
        this.packr.buffer[0] = Protocol.ROOM_DATA;
        if (typeof (type) === "string") {
            encode.string(this.packr.buffer, type, it);
        }
        else {
            encode.number(this.packr.buffer, type, it);
        }
        // force packr to use beginning of the buffer
        this.packr.position = 0;
        const data = (message !== undefined)
            ? this.packr.pack(message, 2048 + it.offset) // 2048 = RESERVE_START_SPACE
            : this.packr.buffer.subarray(0, it.offset);
        this.connection.sendUnreliable(data);
    }
    sendBytes(type, bytes) {
        const it = { offset: 1 };
        this.packr.buffer[0] = Protocol.ROOM_DATA_BYTES;
        if (typeof (type) === "string") {
            encode.string(this.packr.buffer, type, it);
        }
        else {
            encode.number(this.packr.buffer, type, it);
        }
        // check if buffer needs to be resized
        // TODO: can we avoid this?
        if (bytes.byteLength + it.offset > this.packr.buffer.byteLength) {
            const newBuffer = new Uint8Array(it.offset + bytes.byteLength);
            newBuffer.set(this.packr.buffer);
            this.packr.useBuffer(newBuffer);
        }
        this.packr.buffer.set(bytes, it.offset);
        // If connection is not open, buffer the message
        if (!this.connection.isOpen) {
            enqueueMessage(this, this.packr.buffer.subarray(0, it.offset + bytes.byteLength));
        }
        else {
            this.connection.send(this.packr.buffer.subarray(0, it.offset + bytes.byteLength));
        }
    }
    get state() {
        return this.serializer.getState();
    }
    removeAllListeners() {
        this.onJoin.clear();
        this.onStateChange.clear();
        this.onError.clear();
        this.onLeave.clear();
        this.onMessageHandlers.events = {};
        if (this.serializer instanceof SchemaSerializer) {
            // Remove callback references
            this.serializer.decoder.root.callbacks = {};
        }
    }
    onMessageCallback(event) {
        const buffer = new Uint8Array(event.data);
        const it = { offset: 1 };
        const code = buffer[0];
        if (code === Protocol.JOIN_ROOM) {
            const reconnectionToken = decode.utf8Read(buffer, it, buffer[it.offset++]);
            this.serializerId = decode.utf8Read(buffer, it, buffer[it.offset++]);
            // Instantiate serializer if not locally available.
            if (!this.serializer) {
                const serializer = getSerializer(this.serializerId);
                this.serializer = new serializer();
            }
            // apply handshake on first join (no need to do this on reconnect)
            if (buffer.byteLength > it.offset && this.serializer.handshake) {
                this.serializer.handshake(buffer, it);
            }
            if (this.joinedAtTime === 0) {
                this.joinedAtTime = Date.now();
                this.onJoin.invoke();
            }
            else {
                console.info(`[Colyseus reconnection]: ${String.fromCodePoint(0x2705)} reconnection successful!`); // ✅
                this.reconnection.isReconnecting = false;
                this.onReconnect.invoke();
            }
            this.reconnectionToken = `${this.roomId}:${reconnectionToken}`;
            // acknowledge successfull JOIN_ROOM
            this.packr.buffer[0] = Protocol.JOIN_ROOM;
            this.connection.send(this.packr.buffer.subarray(0, 1));
            // Send any enqueued messages that were buffered while disconnected
            if (this.reconnection.enqueuedMessages.length > 0) {
                for (const message of this.reconnection.enqueuedMessages) {
                    this.connection.send(message.data);
                }
                // Clear the buffer after sending
                this.reconnection.enqueuedMessages = [];
            }
        }
        else if (code === Protocol.ERROR) {
            const code = decode.number(buffer, it);
            const message = decode.string(buffer, it);
            this.onError.invoke(code, message);
        }
        else if (code === Protocol.LEAVE_ROOM) {
            this.leave();
        }
        else if (code === Protocol.ROOM_STATE) {
            this.serializer.setState(buffer, it);
            this.onStateChange.invoke(this.serializer.getState());
        }
        else if (code === Protocol.ROOM_STATE_PATCH) {
            this.serializer.patch(buffer, it);
            this.onStateChange.invoke(this.serializer.getState());
        }
        else if (code === Protocol.ROOM_DATA) {
            const type = (decode.stringCheck(buffer, it))
                ? decode.string(buffer, it)
                : decode.number(buffer, it);
            const message = (buffer.byteLength > it.offset)
                ? unpack(buffer, { start: it.offset })
                : undefined;
            this.dispatchMessage(type, message);
        }
        else if (code === Protocol.ROOM_DATA_BYTES) {
            const type = (decode.stringCheck(buffer, it))
                ? decode.string(buffer, it)
                : decode.number(buffer, it);
            this.dispatchMessage(type, buffer.subarray(it.offset));
        }
        else if (code === Protocol.PING) {
            this.#pingCallback?.(Math.round(now() - this.#lastPingTime));
            this.#pingCallback = undefined;
        }
    }
    dispatchMessage(type, message) {
        const messageType = this.getMessageHandlerKey(type);
        if (this.onMessageHandlers.events[messageType]) {
            this.onMessageHandlers.emit(messageType, message);
        }
        else if (this.onMessageHandlers.events['*']) {
            this.onMessageHandlers.emit('*', type, message);
        }
        else if (!messageType.startsWith("__")) { // ignore internal messages
            console.warn?.(`@colyseus/sdk: onMessage() not registered for type '${type}'.`);
        }
    }
    destroy() {
        if (this.serializer) {
            this.serializer.teardown();
        }
    }
    getMessageHandlerKey(type) {
        switch (typeof (type)) {
            // string
            case "string": return type;
            // number
            case "number": return `i${type}`;
            default: throw new Error("invalid message type.");
        }
    }
    handleReconnection() {
        if (Date.now() - this.joinedAtTime < this.reconnection.minUptime) {
            console.info(`[Colyseus reconnection]: ${String.fromCodePoint(0x274C)} Room has not been up for long enough for automatic reconnection. (min uptime: ${this.reconnection.minUptime}ms)`); // ❌
            return;
        }
        if (!this.reconnection.isReconnecting) {
            this.reconnection.retryCount = 0;
            this.reconnection.isReconnecting = true;
        }
        this.retryReconnection();
    }
    retryReconnection() {
        this.reconnection.retryCount++;
        const delay = Math.min(this.reconnection.maxDelay, Math.max(this.reconnection.minDelay, this.reconnection.backoff(this.reconnection.retryCount, this.reconnection.delay)));
        console.info(`[Colyseus reconnection]: ${String.fromCodePoint(0x023F3)} will retry in ${(delay / 1000).toFixed(1)} seconds...`); // 🔄
        // Wait before attempting reconnection
        setTimeout(() => {
            try {
                console.info(`[Colyseus reconnection]: ${String.fromCodePoint(0x1F504)} Re-establishing sessionId '${this.sessionId}' with roomId '${this.roomId}'... (attempt ${this.reconnection.retryCount} of ${this.reconnection.maxRetries})`); // 🔄
                this.connection.reconnect({
                    reconnectionToken: this.reconnectionToken.split(":")[1],
                    skipHandshake: true, // we already applied the handshake on first join
                });
            }
            catch (e) {
                console.log(".reconnect() failed", e);
                if (this.reconnection.retryCount < this.reconnection.maxRetries) {
                    this.retryReconnection();
                }
                else {
                    console.info(`[Colyseus reconnection]: ${String.fromCodePoint(0x274C)} Failed to reconnect. Is your server running? Please check server logs.`); // ❌
                }
            }
        }, delay);
    }
}
const exponentialBackoff = (attempt, delay) => {
    return Math.floor(Math.pow(2, attempt) * delay);
};
function enqueueMessage(room, message) {
    room.reconnection.enqueuedMessages.push({ data: message });
    if (room.reconnection.enqueuedMessages.length > room.reconnection.maxEnqueuedMessages) {
        room.reconnection.enqueuedMessages.shift();
    }
}

export { Room };
//# sourceMappingURL=Room.mjs.map
